import { Search } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Field {
    name: string;
    label: string;
    placeholder?: string;
    inputMode?: "numeric" | "text";
}

interface LookupFormProps<F extends readonly Field[]> {
    fields: F;
    onSubmit: (values: Record<F[number]["name"], string>) => void;
}

/** A row of text inputs that submits trimmed values once every one is filled. */
export function LookupForm<F extends readonly Field[]>({ fields, onSubmit }: LookupFormProps<F>) {
    const [values, setValues] = useState<Record<string, string>>({});
    const trimmed = Object.fromEntries(fields.map(({ name }) => [name, (values[name] ?? "").trim()]));
    const complete = fields.every(({ name }) => trimmed[name] !== "");

    return (
        <Card className="p-3 sm:p-4">
            <form
                className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end"
                onSubmit={(event) => {
                    event.preventDefault();
                    if (complete) onSubmit(trimmed as Record<F[number]["name"], string>);
                }}
            >
                {fields.map(({ name, label, placeholder, inputMode }) => (
                    <div key={name} className="space-y-1.5 sm:min-w-48 sm:flex-1">
                        <Label htmlFor={`lookup-${name}`}>{label}</Label>
                        <Input
                            id={`lookup-${name}`}
                            placeholder={placeholder}
                            inputMode={inputMode}
                            value={values[name] ?? ""}
                            onChange={(event) => setValues((prev) => ({ ...prev, [name]: event.target.value }))}
                        />
                    </div>
                ))}
                <Button type="submit" className="w-full sm:w-auto" disabled={!complete}>
                    <Search />
                    Search
                </Button>
            </form>
        </Card>
    );
}
